import React, { useState, useEffect } from 'react';
import { useCompletedJobs } from '../hooks/useCompletedJobs';
import { useSchedulePermissions } from '../hooks/useSchedulePermissions';
import EditJobModal from '../components/EditJobModal';
import { useAuth } from '../contexts/AuthContext';
import JobsColumn from '../components/schedule/layout/JobsColumn';
import JobCard from '../components/schedule/job-cards/JobCard';
import JobBadges from '../components/schedule/indicators/JobBadges';
import EditButton from '../components/schedule/indicators/EditButton';
import { Link } from 'react-router-dom';
import { format, startOfDay, endOfDay } from 'date-fns';

export default function CompletedJobs() {
  const { user } = useAuth() || {};
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: new Date(),
    endDate: new Date()
  });
  
  // Get completed jobs with date filtering
  const {
    completedJobs,
    completedJobsLoading,
    isLoadingMore,
    hasMore,
    handleLoadMore,
    refetchCompletedJobs,
    totalCount
  } = useCompletedJobs(user, searchTerm, selectedDateRange);
  
  // Reuse permissions hook for job editing
  const {
    onJobEdit,
    canEditJobs,
    editingJob,
    closeEditModal
  } = useSchedulePermissions();
  
  // Handle job updates via modal
  const handleJobUpdate = () => {
    refetchCompletedJobs();
  };
  
  // Handle load more pagination
  const handleLoadMoreJobs = () => {
    handleLoadMore();
  };
  
  // Handle date range changes
  const handleDateRangeChange = (newDateRange) => {
    setSelectedDateRange(newDateRange);
  };
  
  // Helper function to create a date in local timezone
  const createLocalDate = (dateString) => {
    if (!dateString) return new Date();
    
    // Parse the date string and create a date in local timezone
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  };
  
  // Reset to today
  const handleResetToToday = () => {
    const today = new Date();
    setSelectedDateRange({
      startDate: today,
      endDate: today
    });
  };
  
  // Handle search changes
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };
  
  // Render action buttons for each job
  const renderJobActions = (job) => (
    <>
      <Link 
        to={`/dashboard/jobs/${job.id}`}
        className="inline-flex items-center px-3 py-1.5 border border-green-500 text-xs font-medium rounded-md text-green-600 bg-white hover:bg-green-50 hover:text-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
      >
        View Details
        <svg className="ml-1.5 h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
        </svg>
      </Link>
      
      {canEditJobs && <EditButton onClick={() => onJobEdit(job)} />}
    </>
  );
  
  // Render completed job card
  const renderCompletedJobCard = (job, index) => (
    <JobCard
      key={job.id}
      job={job}
      index={index}
      bgColor="bg-green-50"
      leftBorderColor="border-green-500"
      renderBadges={(job) => <JobBadges job={job} allJobs={Object.values(completedJobs).flat()} />}
      renderActions={(job) => renderJobActions(job)}
    />
  );
  
  // Format date range for display
  const formatDateRangeDisplay = () => {
    const { startDate, endDate } = selectedDateRange;
    if (startDate.toDateString() === endDate.toDateString()) {
      return format(startDate, 'MMMM do, yyyy');
    }
    return `${format(startDate, 'MMM do')} - ${format(endDate, 'MMM do, yyyy')}`;
  };
  
  // Check if showing today's jobs
  const isShowingToday = () => {
    const today = new Date();
    const { startDate, endDate } = selectedDateRange;
    return startDate.toDateString() === today.toDateString() && 
           endDate.toDateString() === today.toDateString();
  };
  
  return (
    <div className="mx-auto pb-6 lg:px-8">
      {/* Header */}
      <div className="mt-1 lg:mt-0 mb-2 lg:mb-6 text-center md:text-left">
        <h1 className="text-2xl font-bold text-white md:text-gray-900">Completed Jobs</h1>
        <p className="mt-1 text-sm text-gray-500">
          Completed jobs for <strong>{user?.firstName || 'User'}{user?.lastName ? ` ${user.lastName}` : ''}</strong>
        </p>
      </div>
      
      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
          {/* Date Range Picker */}
          <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Date Range:
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="date"
                value={format(selectedDateRange.startDate, 'yyyy-MM-dd')}
                onChange={(e) => handleDateRangeChange({
                  ...selectedDateRange,
                  startDate: createLocalDate(e.target.value)
                })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={format(selectedDateRange.endDate, 'yyyy-MM-dd')}
                onChange={(e) => handleDateRangeChange({
                  ...selectedDateRange,
                  endDate: createLocalDate(e.target.value)
                })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-green-500 focus:border-green-500 sm:text-sm"
              />
            </div>
            {!isShowingToday() && (
              <button
                onClick={handleResetToToday}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Today
              </button>
            )}
          </div>
          
          {/* Search */}
          <div className="w-full lg:w-auto lg:max-w-xs">
            <div className="relative rounded-md shadow-sm">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="focus:ring-green-500 focus:border-green-500 block w-full pl-10 pr-10 py-2 sm:text-sm border-gray-300 rounded-md"
                placeholder="Search properties..."
              />
              {searchTerm && (
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button 
                    onClick={() => handleSearchChange('')}
                    className="text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Jobs Display */}
      <div className="space-y-8">
        <JobsColumn
          title={`Completed Jobs for ${formatDateRangeDisplay()}`}
          jobsByDate={completedJobs}
          loading={completedJobsLoading}
          renderJobCard={renderCompletedJobCard}
          badgeColor="bg-green-100 text-green-800"
          spinnerColor="border-green-600"
          emptyStateProps={{
            title: "No completed jobs",
            description: completedJobsLoading 
              ? "Loading jobs..." 
              : searchTerm 
                ? `No completed jobs match your search for "${searchTerm}"`
                : `No jobs were completed for ${formatDateRangeDisplay()}`,
            icon: "check-circle"
          }}
          defaultExpanded={true}
          containerClassName="h-auto"
          totalCount={totalCount}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          onLoadMore={handleLoadMoreJobs}
        />
      </div>

      {/* Edit Job Modal */}
      {editingJob && (
        <EditJobModal 
          job={editingJob} 
          onClose={closeEditModal} 
          onUpdate={handleJobUpdate} 
        />
      )}
    </div>
  );
} 